import DepartmentsPicker from 'views/Users/common/DepartmentsPicker';
import React from 'react';
import { BodyS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import SimpleTable from 'components/SimpleTable/SimpleTable';
import Business from '@material-ui/icons/Business';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/addUserDialogStyle';
import { makeStyles } from '@material-ui/core';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { MAX_ADMIN_COUNT } from 'utils/const/users';
import moment from 'moment';
import { isValidDate } from 'utils/date/validateDate';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IExtendedSingleCorpAccount extends SingleCorpAccount {
	disabled: boolean;
}

interface IDepartmentPermissionsTabProps {
	departments: SingleDepartment[];
	accounts: SingleCorpAccount[];
	selectedUserType: types.users.userType;
	setUnlimitedExpirationDate: React.Dispatch<React.SetStateAction<boolean>>;
	unlimitedExpirationDate: boolean;
	setExpirationDate: (date: moment.Moment | null) => void;
	expirationDate: moment.Moment | null;
	selectedAccounts: string[];
	setSelectedAccounts: (accounts: string[]) => void;
	assignedAccount: types.users.IAssignedAccount | null;
	selectedDepartments: string[];
	setSelectedDepartments: (value: string[]) => void;
}

const DepartmentPermissionsTab = ({
	departments,
	accounts,
	selectedUserType,
	setUnlimitedExpirationDate,
	unlimitedExpirationDate,
	setExpirationDate,
	expirationDate,
	selectedAccounts,
	setSelectedAccounts,
	assignedAccount,
	selectedDepartments,
	setSelectedDepartments,
}: IDepartmentPermissionsTabProps) => {
	const classes = useStyles();
	const isCorporate = accounts.length === 1;
	const allAccountsDisabled = accounts.every((account) => Number(account.adminsCount) >= MAX_ADMIN_COUNT);
	const someAccountsDisabled = accounts.some((account) => Number(account.adminsCount) >= MAX_ADMIN_COUNT);

	return allAccountsDisabled && selectedUserType === 'ADMIN' ? (
		<div className={classes.maxAdminCount}>
			<BodyS>{translate('add.user.you.reach.max.admins')}</BodyS>
			<BodyS>{translate('add.user.you.can.create.without.permissions')}</BodyS>
		</div>
	) : (
		<div className={classes.expirationDateWrapper}>
			<BodyS color="secondary-light">{translate('select.expiration.date')}</BodyS>
			<div>
				<CustomDatePicker
					timeFormat={false}
					value={expirationDate}
					onChange={(date) => {
						if (moment.isMoment(date)) {
							setExpirationDate(date);
							setUnlimitedExpirationDate(false);
						}
					}}
					inputProps={{
						placeholder: translate('department.details.expiration.date'),
					}}
					isValidDate={(currentDate) => isValidDate(currentDate, 'yesterday')}
				/>
				<CustomCheckbox
					label={translate('unlimited')}
					checked={unlimitedExpirationDate}
					onClick={() => {
						setUnlimitedExpirationDate((value: boolean) => !value);
						setExpirationDate(null);
					}}
				/>
			</div>
			<div className={classes.accountsWrapper}>
				{selectedUserType === 'ADMIN' && (
					<>
						<BodyS color="secondary-light">
							{isCorporate
								? translate('accounts.assign.full-access')
								: translate('accounts.assign.permissions')}
						</BodyS>
						{assignedAccount && accounts && accounts.length > 1 && (
							<>
								<SimpleTable
									data={accounts.map((account) => ({
										...account,
										disabled: Number(account.adminsCount) >= MAX_ADMIN_COUNT,
									}))}
									idAccessor="accountId"
									columns={[
										{
											header: (
												<div className={classes.selectedItem}>
													<Business />
													<BodyS>
														{accounts.length > 3
															? translate('all.organizations')
															: translate('organization.name')}
													</BodyS>
												</div>
											),
											accessor: 'accountName',
											cell: (row: IExtendedSingleCorpAccount) => (
												<div>
													<BodyS disableMargin>
														<>
															{row.accountName}
															{row.disabled && '*'}
														</>
													</BodyS>
												</div>
											),
										},
									]}
									selectedItems={selectedAccounts}
									setSelectedItems={(value) => setSelectedAccounts(value)}
									withCheckboxes
								/>
								{someAccountsDisabled && (
									<div className={classes.adminsCountInfo}>
										<BodyS color="secondary-light" disableMargin>
											{translate('add.user.max.amount.reached')}
										</BodyS>
									</div>
								)}
							</>
						)}
					</>
				)}
				{selectedUserType === 'MANAGER' && (
					<DepartmentsPicker
						accounts={accounts.map(
							(account: SingleCorpAccount): types.users.ISingleAccount => ({
								id: account.accountId,
								name: account.accountName,
								adminsCount: Number(account.adminsCount),
							})
						)}
						departments={departments}
						selectedDepartments={selectedDepartments}
						setSelectedDepartments={(items) => setSelectedDepartments(items)}
					/>
				)}
			</div>
		</div>
	);
};

export default DepartmentPermissionsTab;
